/* ── Enums ─────────────────────────────────────────────────── */

export enum UserRole {
  USER = 'user',
  DIGA = 'diga',
  ADMIN = 'admin',
}

export enum ScanStatus {
  PENDING = 'pending',
  RUNNING = 'running',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

/**
 * Well-known keys of rows in `scan_labels`. Immer `key_name` zum Adressieren nutzen,
 * nie die numerische `id`.
 */
export const SCAN_LABEL_KEYS = {
  SCAN_CREATED: 'SCAN_CREATED',
  TASK_CREATION_FAILED: 'TASK_CREATION_FAILED',
  TASK_QUEUED: 'TASK_QUEUED',
  TASK_PROCESSING: 'TASK_PROCESSING',
  CMP_DETECTION: 'CMP_DETECTION',
  CMP_DETECTION_AI: 'CMP_DETECTION_AI',
  TRACKING_CAPTURE: 'TRACKING_CAPTURE',
  WORKFLOW_UNREACHABLE: 'WORKFLOW_UNREACHABLE',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
  /**
   * n8n hat die Rohdaten in `score_cards.n8n_raw` abgelegt und `scans.label_id`
   * auf diesen Wert gesetzt. Der Backend-Worker pickt solche Scans auf, laesst
   * die Evaluation laufen und schaltet auf COMPLETED / FAILED weiter.
   */
  DATA_ANALYSING: 'DATA_ANALYSING',
} as const;

export type ScanLabelKey = (typeof SCAN_LABEL_KEYS)[keyof typeof SCAN_LABEL_KEYS];

export const TERMINAL_SCAN_LABEL_KEYS: readonly ScanLabelKey[] = [
  SCAN_LABEL_KEYS.COMPLETED,
  SCAN_LABEL_KEYS.FAILED,
  SCAN_LABEL_KEYS.TASK_CREATION_FAILED,
  SCAN_LABEL_KEYS.WORKFLOW_UNREACHABLE,
];

export interface IScanLabel {
  id: number;
  key_name: ScanLabelKey | string;
  emoji: string | null;
  label: string;
  sort_order: number;
}

/**
 * Reduziertes ScanLabel fuer Polling / Chip-Anzeige. `id` / `sort_order` sind
 * in diesem Kontext nicht relevant und werden vom Server weggelassen.
 */
export type ScanLabelSummary = Pick<IScanLabel, 'key_name' | 'emoji' | 'label'>;

export interface IActiveScanStatus {
  id: number;
  url: string;
  created_at: string;
  scan_label: ScanLabelSummary | null;
}

export enum Weight {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
}

export const WEIGHT_VALUE: Record<Weight, number> = {
  [Weight.LOW]: 1,
  [Weight.MEDIUM]: 5,
  [Weight.HIGH]: 10,
};

export enum Rating {
  G = 'G',
  Y = 'Y',
  R = 'R',
}

export const RATING_SCORE_FACTOR: Record<Rating, number> = {
  [Rating.G]: 1.0,
  [Rating.Y]: 0.5,
  [Rating.R]: 0.0,
};

export enum CategoryScope {
  DOMAIN_SCREENER = 'domain_screener',
  TRACKING_WATCHDOG = 'tracking_watchdog',
}

/* ── Konfiguration+ Enums ────────────────────────────────── */

export enum EngineMode {
  LEGACY = 'legacy',
  RULE_ENGINE = 'rule_engine',
}

export enum MatchType {
  EXACT = 'exact',
  CONTAINS = 'contains',
  REGEX = 'regex',
}

export enum DataFieldSource {
  CMP = 'cmp',
  NETWORK = 'network',
  DATALAYER = 'datalayer',
  META = 'meta',
  CONSENT = 'consent',
  SCRIPTS = 'scripts',
}

export enum DataFieldType {
  BOOLEAN = 'boolean',
  STRING = 'string',
  NUMBER = 'number',
  ARRAY = 'array',
  OBJECT = 'object',
}

export enum OperatorPhase {
  MVP = 'mvp',
  PHASE2 = 'phase2',
}

export enum ImpactDirection {
  PLUS = '+',
  MINUS = '-',
}

export type LogicOperator = 'AND' | 'OR';

/* ── Entity interfaces ────────────────────────────────────── */

export interface IUser {
  id: string;
  email: string;
  password_hash: string | null;
  name: string;
  role: UserRole;
  active: boolean;
  dev_mode: boolean;
  last_login_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface IMagicLink {
  id: number;
  token: string;
  user_id: string;
  expires_at: string;
  used_at: string | null;
  created_at: string;
}

export interface ISession {
  id: number;
  refresh_token: string;
  user_id: string;
  user_agent: string | null;
  ip_address: string | null;
  revoked_at: string | null;
  expires_at: string;
  created_at: string;
}

export interface ICategory {
  id: string;
  internal_name: string;
  display_name: string;
  description: string | null;
  color: string | null;
  slot: number | null;
  scope: CategoryScope;
  engine_mode: EngineMode;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ILabel {
  id: number;
  name: string;
  color: string | null;
}

export interface ICheck {
  id: string;
  check_code: string;
  title: string;
  description: string | null;
  info_box: string | null;
  explanation: string | null;
  recommendation_green: string | null;
  recommendation_yellow: string | null;
  recommendation_red: string | null;
  quick_win_flag: boolean;
  category_id: string;
  weight: Weight;
  active: boolean;
  engine_mode: EngineMode;
  created_at: string;
  updated_at: string;
  category?: ICategory;
  scan_levels?: IScanLevel[];
  playwright_inputs?: IPlaywrightInput[];
  mapping_rules?: IMappingRule[];
  check_packs?: ICheckPack[];
  labels?: ILabel[];
  rule_groups?: ICheckRuleGroup[];
}

export interface ICheckPlus {
  id: string;
  check_code: string;
  title: string;
  description: string | null;
  info_box: string | null;
  /** Kein Treffer in den Regelgruppen = Rot (Standard) statt kein Rating. */
  auto_red_fallback?: boolean;
  category_id: string;
  weight: Weight;
  weight_value: number;
  impact: string | null;
  active: boolean;
  dev_only?: boolean;
  engine_mode: EngineMode;
  created_at: string;
  updated_at: string;
  category?: ICategory;
  labels?: ILabel[];
  rule_groups?: ICheckRuleGroup[];
}

export interface IScanLevel {
  id: number;
  name: string;
}

export interface IPlaywrightInput {
  id: number;
  name: string;
  CheckPlaywrightInput?: { data_path: string | null };
}

export interface ICheckScanLevel {
  check_id: string;
  scan_level_id: number;
}

export interface ICheckPlaywrightInput {
  check_id: string;
  playwright_input_id: number;
  data_path: string | null;
}

export interface IMappingRule {
  id: number;
  check_id: string;
  output_condition: string;
  rating: Rating;
  score_factor: number;
  sort_order: number;
  active?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface IQuickWin {
  id: number;
  title: string;
  description: string | null;
  active: boolean;
  created_at: string;
  updated_at: string;
  triggers?: IQuickWinTrigger[];
}

export interface IQuickWinTrigger {
  quick_win_id: number;
  check_id: string;
  trigger_rating: Rating;
}

export interface IDisclaimer {
  id: number;
  content: string;
  updated_by: string | null;
  updated_at: string;
  created_at?: string;
}

export interface IScan {
  id: number;
  url: string;
  /** FK auf scan_labels. Source of Truth fuer den Scan-Status. */
  label_id?: number | null;
  /** Fehlertext aus n8n / Cloud-Tasks. */
  error_message?: string | null;
  user_id: string;
  scan_template_id: number | null;
  is_public: boolean;
  /** Nur wenn die DB-Spalten existieren (lokales Schema). */
  started_at?: string | null;
  completed_at?: string | null;
  created_at: string;
  /** Fehlt in manchen Cloud-Schemas (nur created_at). */
  updated_at?: string;
  score_card?: IScoreCard;
  scan_label?: IScanLabel | ScanLabelSummary | null;
  user?: { name: string; email: string };
  scan_template?: {
    id: number;
    display_name: string;
    score_card_html_template?: string | null;
    source?: 'legacy' | 'config_plus';
  } | null;
}

export interface ICategoryScore {
  category_id: string;
  category_name: string;
  score: number;
  rating: Rating;
}

export interface IScoreCard {
  id: number;
  scan_id: number;
  /**
   * Rohdaten direkt aus dem n8n-Workflow. Der Worker liest dieses Feld, wenn
   * der Scan auf DATA_ANALYSING steht, und setzt `scores_json` daraus.
   * Typisch liefert n8n ein Array mit einem Root-Objekt (`consent_accepted` + `consent_denied`).
   */
  n8n_raw: unknown | null;
  playwright_raw: Record<string, unknown> | null;
  scores_json: {
    overall_score: number;
    overall_rating: Rating;
    categories: ICategoryScore[];
    check_results?: ICheckEval[];
  } | null;
  share_token: string | null;
  /** Nur wenn die DB-Spalte existiert. */
  share_expires_at?: string | null;
  visible_in_archive: boolean;
  created_at: string;
  updated_at?: string;
  quick_wins?: IQuickWin[];
}

export interface IScanTemplate {
  id: number;
  internal_name: string;
  display_name: string;
  description: string | null;
  is_active: boolean;
  is_default: boolean;
  emoji: string | null;
  /** Share-Score-Card HTML-Vorlage (z. B. ambition-default). */
  score_card_html_template?: string | null;
  /** Nur für Admins/Devs sichtbar, wenn dev_mode aktiv. */
  dev_only?: boolean;
  source: 'legacy' | 'config_plus';
  created_by: string | null;
  created_at: string;
  updated_at: string;
  categories?: IScanTemplateCategory[];
  check_packs?: IScanTemplateCheckPack[];
  template_categories?: IScanTemplateCategory[];
  template_check_packs?: IScanTemplateCheckPack[];
}

export interface ICheckPack {
  id: number;
  internal_name: string;
  display_name: string;
  description: string | null;
  category_id: string;
  is_active: boolean;
  emoji: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  category?: ICategory;
  checks?: ICheck[];
  pack_checks?: ICheckPackCheck[];
}

export interface ICheckPackCheck {
  check_pack_id: number;
  check_id: string;
  sort_order: number;
  check?: ICheck;
}

export interface IScanTemplateCategory {
  scan_template_id: number;
  category_id: string;
  slot: number;
  is_active: boolean;
  category?: ICategory;
}

export interface IScanTemplateCheckPack {
  scan_template_id: number;
  check_pack_id: number;
  is_active: boolean;
  check_pack?: ICheckPack;
}

export interface IAuditLogEntry {
  id: number;
  user_id: string;
  user_email: string;
  action: string;
  entity_type: string;
  entity_id: number | null;
  diff: { before: unknown; after: unknown } | null;
  created_at: string;
}

export interface IAppSetting {
  key: string;
  value: string;
  updated_at: string;
}

/* ── Slack-Benachrichtigungen ─────────────────────────────── */

/**
 * Zwei getrennte Kanaele, weil sie unterschiedliche Leser haben: die
 * Watchdog-Alarme gehen an DigA (Kunde ist betroffen), die Bug-Reports an die
 * Entwicklung. Jeder Kanal hat seinen eigenen Webhook und seinen eigenen
 * An/Aus-Schalter, damit man den einen abstellen kann ohne den anderen.
 */
export const SLACK_CHANNELS = ['watchdog', 'bug_report'] as const;
export type SlackChannel = (typeof SLACK_CHANNELS)[number];

/**
 * `incoming` = klassischer Incoming Webhook (`/services/…`, braucht eine Slack-App, nimmt Block Kit).
 * `trigger`  = Workflow-Builder-Trigger (`/triggers/…`, ohne App anlegbar, nimmt nur flache String-Variablen).
 * `null`     = noch keine URL hinterlegt.
 */
export type SlackWebhookKind = 'incoming' | 'trigger';

export interface ISlackChannelConfig {
  channel: SlackChannel;
  label: string;
  description: string;
  enabled: boolean;
  /** Aus der hinterlegten URL erkannt — bestimmt das Payload-Format. */
  webhook_kind: SlackWebhookKind | null;
  /** Nur maskiert (`https://hooks.slack.com/services/…/XXXX1234`) — der Klartext verlaesst den Server nie. */
  webhook_url_masked: string | null;
  /** Ob ueberhaupt eine URL hinterlegt ist. Ohne URL wird nie gesendet, egal was `enabled` sagt. */
  webhook_configured: boolean;
  /** Die URL kommt aus der .env statt aus den App-Settings und ist dann hier nicht editierbar. */
  from_env: boolean;
}

export interface ISlackConfigResponse {
  channels: ISlackChannelConfig[];
}

export interface UpdateSlackChannelRequest {
  enabled?: boolean;
  /** Leerstring loescht die hinterlegte URL. */
  webhook_url?: string;
}

/** Ergebnis eines Sendeversuchs — auch der bewusst unterlassene ist ein Ergebnis. */
export interface ISlackSendResult {
  sent: boolean;
  /** Gefuellt wenn `sent === false`: warum nicht (disabled, keine URL, HTTP-Fehler). */
  reason?: string;
  /** Was rausgegangen waere bzw. ist — dasselbe JSON wie in der Vorschau. */
  payload: unknown;
}

export interface IBugReportRequest {
  /** Wo ist es passiert — vorbelegt mit dem aktuellen Pfad, vom Nutzer aenderbar. */
  page: string;
  description: string;
  /** Data-URLs (`data:image/png;base64,…`) aus der Zwischenablage. */
  screenshots?: string[];
}

/* ── Konfiguration+ Entity interfaces ────────────────────── */

export interface IAliasGroup {
  id: string;
  canonical_name: string;
  description: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  aliases?: IAlias[];
  creator?: { name: string; email: string };
}

export interface IAlias {
  id: string;
  alias_group_id: string;
  alias_value: string;
  match_type: MatchType;
  created_at: string;
}

export interface IDataField {
  id: number;
  source: DataFieldSource;
  field_path: string;
  display_name: string;
  data_type: DataFieldType;
  description: string | null;
  active: boolean;
}

export interface IRuleOperator {
  id: number;
  operator_key: string;
  display_label: string;
  allowed_types: string[] | null;
  phase: OperatorPhase;
}

export interface ICheckRuleGroup {
  id: string;
  check_id: string;
  logic_operator: LogicOperator;
  result_rating: Rating;
  sort_order: number;
  rules?: ICheckRule[];
}

export interface ICheckRule {
  id: string;
  rule_group_id: string;
  data_field_id: number;
  operator_id: number;
  expected_value: string | null;
  sort_order: number;
  data_field?: IDataField;
  operator?: IRuleOperator;
}

export interface IScoringRule {
  id: string;
  name: string;
  description: string | null;
  impact_level: number;
  is_showstopper: boolean;
  active: boolean;
  dev_only?: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  conditions?: IScoringRuleCondition[];
  consequences?: IScoringRuleConsequence[];
  /** Erklaertexte je Fall — kommt aus `scoring_rule_branches`, fehlt bei Legacy-Regeln ohne Faelle. */
  branches?: IScoringRuleBranchMeta[];
  creator?: { name: string; email: string };
}

export interface IScoringRuleCondition {
  id: string;
  scoring_rule_id: string;
  branch_id: string | null;
  check_id: string;
  expected_rating: string;
  sort_order: number;
  check?: ICheck;
  check_plus?: ICheckPlus;
}

export interface IScoringRuleConsequence {
  id: string;
  scoring_rule_id: string;
  branch_id: string | null;
  category_id: string;
  impact_direction: ImpactDirection;
  impact_percent: number;
  sort_order: number;
  category?: ICategory;
}

/** One IF→THEN branch within a scoring rule (conditions share branch_id with their consequences). */
export interface IScoringRuleBranch {
  branch_id: string;
  conditions: IScoringRuleCondition[];
  consequences: IScoringRuleConsequence[];
}

/**
 * Zeile aus `scoring_rule_branches`: der Erklaertext eines Falls plus sein Schalter.
 * Getrennt von `IScoringRuleBranch` (reine Gruppierungshilfe fuer die Anzeige), weil
 * das hier persistierte Daten sind.
 */
export interface IScoringRuleBranchMeta {
  branch_id: string;
  scoring_rule_id: string;
  explanation_active: boolean;
  explanation: string | null;
  /** Die Handlungsempfehlung dieses Falls darf als Quick Win auf die Scorecard. Fehlt, solange die Spalte nicht eingespielt ist. */
  quick_win?: boolean;
  sort_order: number;
}

export interface ITemplateScoringRule {
  template_id: number;
  scoring_rule_id: string;
  scoring_rule?: IScoringRule;
}

/* ── Evaluation types (in-memory, not persisted) ─────────── */

export type EvalConfidence = 'high' | 'medium' | 'low';

export interface ICheckEval {
  check_code: string;
  check_id: string;
  title: string;
  description: string | null;
  info_box?: string | null;
  explanation?: string | null;
  /**
   * Woher `explanation` stammt. Nur `'scoring_branch'` wird in der Auswertung angezeigt.
   *
   * Bis 08/2026 trug jede Zeile den Erklaertext ihres Checks — in der Auswertung war das
   * die falsche Erklaerung, weil die sichtbare Zeile die Scoring-Regel ist. Historische
   * Scorecards haben diesen Text in `scores_json` einbetoniert; ohne diese Markierung
   * liesse er sich beim Anzeigen nicht von einem echten Fall-Text unterscheiden.
   * Fehlt das Feld (alte Scans), wird nichts angezeigt.
   */
  explanation_source?: 'scoring_branch';
  category_id: string;
  category_name: string;
  weight: Weight;
  rating: Rating;
  score_factor: number;
  matched_rule: string | null;
  raw_value: unknown;
  skipped: boolean;
  /** How confident the engine is in this result. 'low' = data was missing or ambiguous. Absent on legacy scans. */
  confidence?: EvalConfidence;
  /**
   * When a check is bound to one or more scoring rules in the template, the engine emits one
   * row per (scoring_rule, branch, consequence_category) binding so each rule contributes
   * exactly one visible result in its target category. These IDs identify that binding.
   * Absent on legacy/unbound rows (display falls back to the check's own category + title).
   */
  scoring_rule_id?: string;
  /**
   * Der Fall der Regel, der gefeuert hat (seit 11/2026). Schluessel der Handlungsempfehlung:
   * an ihm haengen Erklaertext und Quick-Win-Markierung (scoring_rule_branches). Fehlt bei
   * aelteren Scans — die zeigen erst nach einer Neuauswertung Quick Wins.
   */
  scoring_branch_id?: string;
  /**
   * @deprecated Engine no longer fans out per branch — one row per scoring rule. Kept
   * optional for backward compatibility with persisted historical scorecards.
   */
  branch_id?: string | null;
}

/**
 * Handlungsempfehlung eines Scans (Konfigurator Plus): der Erklaertext eines gefeuerten
 * Falls. Bis zu drei aktive stehen als Quick Wins auf der Scorecard.
 */
export interface IScanRecommendation {
  /** branch_id des Falls */
  key: string;
  rule_id: string;
  /** Name der Scoring-Regel */
  title: string;
  category_id: string;
  category_name: string;
  /** Die Handlungsempfehlung (Erklaertext des Falls) */
  text: string;
  rating: Rating;
  /** Der Fall ist im Scoring-Editor als Quick Win markiert — daraus waehlt das System vor. */
  quick_win: boolean;
  /** Steht als Quick Win auf der Scorecard. */
  active: boolean;
}

export interface ICategoryEval {
  category_id: string;
  display_name: string;
  color: string | null;
  score: number;
  base_score?: number;
  adjustments?: Array<{
    rule_name: string;
    direction: '+' | '-';
    impact_percent: number;
  }>;
  rating: Rating;
  checks_total: number;
  checks_green: number;
  checks_yellow: number;
  checks_red: number;
}

export interface IScoringRuleImpact {
  rule_id: string;
  rule_name: string;
  matched: boolean;
  impact_level: number;
  is_showstopper: boolean;
  adjustments: Array<{
    category_id: string;
    direction: '+' | '-';
    percent: number;
    rule_name: string;
    is_showstopper: boolean;
  }>;
}

export interface IEvaluationResult {
  overall_score: number;
  overall_rating: Rating;
  categories: ICategoryEval[];
  check_results: ICheckEval[];
  scoring_impacts?: IScoringRuleImpact[];
  evaluated_at: string;
  /**
   * Map of `check_id → Rating` for EVERY check the engine evaluated in this scan,
   * regardless of fan-out / suppression / rule-binding. Downstream consumers (e.g.
   * Quick Wins) MUST read from this map rather than `check_results` once the engine
   * collapses fan-out to per-rule rows. Absent on legacy persisted scorecards →
   * consumers should fall back to deriving from `check_results`.
   */
  check_ratings?: Record<string, Rating>;
  /**
   * Map of `check_id → native category display name` (the check's OWN category,
   * pre-override) for every evaluated check. Used by Quick Wins to bucket triggered
   * suggestions by the check's true category, since `check_results.category_name`
   * may now carry a scoring-rule's primary category instead of the check's own.
   * Absent on legacy persisted scorecards → fall back to `check_results.category_name`.
   */
  check_categories?: Record<string, string>;
}

/* ── API types ────────────────────────────────────────────── */

export interface ApiSuccessResponse<T = unknown> {
  success: true;
  data: T;
}

export interface ApiErrorResponse {
  success: false;
  error: {
    code: string;
    message: string;
  };
}

export type ApiResponse<T = unknown> = ApiSuccessResponse<T> | ApiErrorResponse;

/* ── Request types ────────────────────────────────────────── */

export interface LoginRequest {
  email: string;
}

export interface LoginPasswordRequest {
  email: string;
  password: string;
}

export interface ScanRequest {
  url: string;
}

export interface CreateUserRequest {
  email: string;
  name: string;
  role: UserRole;
  password?: string;
}

/* ── Auth payload carried on req.user ─────────────────────── */

export interface JwtPayload {
  userId: string;
  email: string;
  role: UserRole;
}
